// <div data-controller="business--plan-with-credit">
//
// </div>
import { Controller } from "@hotwired/stimulus";
import api from "@/utils/api";
import * as helpers from "@/utils/helpers";

// https://stimulus.hotwired.dev/reference
export default class extends Controller {
  static targets = [
      "planTypeInput", "creditPlanSelect", "paymentCycleInput",
      "priceDescription", "extraCreditDescription", "referralCouponDiscount",
      "cleanUpAgreement",
      "cleanUpAgreementCheckBox",
      "brandingPlanFeature",
      "marketingPlanFeature",
      "trialCreditsReset",
      "trialCreditsResetAgreementCheckBox",
      "businessAgreement",
      "businessAgreementCheckBox"
  ];
  static classes = [ "" ]
  static values = {
    planTypeCreditPlanOptions: Object,
    creditPlanMapping: Object,
    yearlyRate: Number,
    currentCreditPlanId: Number,
    persistedPlanType: String,
    isPersisted: Boolean,
    subscriptionPlanType: String,
    isTrialPeriod: Boolean,
    referralCoupon: Object
  };

  initialize() {

  }

  get currentPlanType() {
    let checkedPlan;
    this.planTypeInputTargets.forEach((target) => {
      if (target.checked) {
        checkedPlan = target.value;
      }
    });
    return checkedPlan;
  }

  get currentPaymentCycle() {
    let checkedPaymentCycle;
    this.paymentCycleInputTargets.forEach((target) => {
      if (target.checked) {
        checkedPaymentCycle = target.value;
      }
    });
    return checkedPaymentCycle;
  }

  get currentCreditPlan() {
    const creditPlanOptions = this.creditPlanSelectTarget.options;
    const selectedCreditPlanId = creditPlanOptions[creditPlanOptions.selectedIndex].value;
    return this.creditPlanMappingValue[selectedCreditPlanId];
  }

  hasValidReferralCoupon() {
    return this.referralCouponValue && 
           this.referralCouponValue.code && 
           this.referralCouponValue.discount_type && 
           this.referralCouponValue.discount_value;
  }

  updateCreditPlanSelectOptions(options) {
    // clear previous options
    while (this.creditPlanSelectTarget.options.length > 0) {
      this.creditPlanSelectTarget.remove(0);
    }

    let hasMatchingDefaultOption;
    options.forEach((option) => {
      const newOption = document.createElement('option');
      const optionText = document.createTextNode(option.text);
      newOption.appendChild(optionText);
      newOption.setAttribute('value', option.value);
      if (this.currentCreditPlanIdValue === option.value) {
        hasMatchingDefaultOption = true;
      }
      this.creditPlanSelectTarget.appendChild(newOption);
    })

    if (hasMatchingDefaultOption) {
      this.creditPlanSelectTarget.value = this.currentCreditPlanIdValue;
    }
  }

  warnPlanTypeDowngrade(show, targetPlan) {
    if (this.subscriptionPlanTypeValue === "branding") {
      if (targetPlan === "basic") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.add("hidden"));
      }
    } else if (this.subscriptionPlanTypeValue === "marketing") {
      if (targetPlan === "basic") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
      } else if (targetPlan === "branding") {
        this.brandingPlanFeatureTargets.forEach((target) => target.classList.add("hidden"));
        this.marketingPlanFeatureTargets.forEach((target) => target.classList.remove("hidden"));
      }
    }

    if (this.hasCleanUpAgreementTarget) {
      this.cleanUpAgreementTarget.classList.toggle("hidden", !show)
      this.cleanUpAgreementCheckBoxTarget.required = show;
    }
  }

  warnTrialPeriodFeature(show) {
    if (this.hasTrialCreditsResetTarget) {
      this.trialCreditsResetTarget.classList.toggle("hidden", !show)
      this.trialCreditsResetAgreementCheckBoxTarget.required = show;
    }
  }

  afterPlanTypeSelected() {
    if (this.currentPlanType === "basic") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.basic);
      this.warnPlanTypeDowngrade(this.isPersistedValue && (this.subscriptionPlanTypeValue !== "basic"), "basic");
    } else if (this.currentPlanType === "branding") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.branding);
      this.warnPlanTypeDowngrade(this.isPersistedValue && (this.subscriptionPlanTypeValue !== "basic" && this.subscriptionPlanTypeValue !== "branding"), "branding");
    } else if (this.currentPlanType === "marketing") {
      this.updateCreditPlanSelectOptions(this.planTypeCreditPlanOptionsValue.marketing);
      this.warnPlanTypeDowngrade(false);
    }

    this.warnTrialPeriodFeature(this.isTrialPeriodValue && (this.subscriptionPlanTypeValue === "basic") && (this.currentPlanType !== "basic"));
    this.calculatePrice();
  }

  afterPaymentCycleSelected() {
    this.calculatePrice();
  }

  afterCreditPlanSelected() {
    this.calculatePrice();
  }

  paymentCycleAvailability(unitPrice) {
    this.paymentCycleInputTargets.forEach((target) => {
      target.disabled = (unitPrice === 0 && (target.value !== "monthly"));
      const targetLabel = document.querySelector(`label[for=${target.id}`)
      targetLabel.classList.toggle("disabled", target.disabled)

      if (unitPrice === 0 && (target.value === "monthly")) {
        target.checked = true;
      }
    })
  }

  toggleBusinessAgreement(show) {
    if (this.hasBusinessAgreementTarget) {
      this.businessAgreementTarget.classList.toggle("hidden", !show)
      this.businessAgreementCheckBoxTarget.checked = false;
      this.businessAgreementCheckBoxTarget.required = show;
    }
  }

  calculatePrice() {
    if (this.currentCreditPlan === undefined || this.currentPaymentCycle === undefined) {
      this.priceDescriptionTarget.innerHTML = "-";
      this.hideReferralCouponDiscount();
      return;
    }

    let label;
    const unitPrice = this.currentCreditPlan.price;
    const credits = this.currentCreditPlan.credits;

    this.toggleBusinessAgreement(unitPrice > 0);
    this.paymentCycleAvailability(unitPrice);

    let finalPrice = unitPrice;
    if (this.currentPaymentCycle === "yearly") {
      finalPrice = this.yearlyRateValue * unitPrice;
    }

    // Calculate referral coupon discount
    let referralDiscount = 0;
    if (this.hasValidReferralCoupon() && finalPrice > 0) {
      if (this.referralCouponValue.discount_type === "percentage") {
        referralDiscount = Math.round(finalPrice * this.referralCouponValue.discount_value / 100);
      } else {
        referralDiscount = Math.min(this.referralCouponValue.discount_value, finalPrice);
      }
    }

    if (this.currentPaymentCycle === "yearly") {
      const originalPrice = this.yearlyRateValue * unitPrice;
      const discountPrice = (12 - this.yearlyRateValue) * unitPrice;
      const discountLabel = `<span class="italic text-xs text-red-600 dark:text-red-300">${helpers.krw(discountPrice)} 연간 결제 할인 적용</span>`;
      
      if (referralDiscount > 0) {
        const discountedPrice = originalPrice - referralDiscount;
        label = `<div>
            <div>첫 해: 연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(discountedPrice)}) ${discountLabel}</div>
            <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">다음 해부터: 연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(originalPrice)}) ${discountLabel}</div>
          </div>`;
      } else {
        label = `연 ${helpers.numberWithDelimiter(credits * 12)}건 (${helpers.krw(originalPrice)}) ${discountLabel}`;
      }
    } else if (this.currentPaymentCycle === "monthly") {
      if (finalPrice === 0) {
        label = this.currentCreditPlan.price_description;
      } else if (referralDiscount > 0) {
        const discountedPrice = finalPrice - referralDiscount;
        label = `<div>
            <div>첫 달: 월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(discountedPrice)})</div>
            <div class="text-sm text-gray-600 dark:text-gray-400 mt-1">다음 달부터: 월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(finalPrice)})</div>
          </div>`;
      } else {
        label = `월 ${helpers.numberWithDelimiter(credits)}건 (${helpers.krw(finalPrice)})`;
      }
    }

    // Show referral coupon discount if applicable
    if (referralDiscount > 0) {
      this.showReferralCouponDiscount();
    } else {
      this.hideReferralCouponDiscount();
    }

    this.extraCreditDescriptionTarget.innerText = `이후 초과되는 발송량에 대해서는 500건 기준 ${helpers.krw(this.currentCreditPlan.credit_price * 500)} 으로 추가 충전이 필요 합니다.`;
    this.priceDescriptionTarget.innerHTML = label;
  }

  showReferralCouponDiscount() {
    if (this.hasReferralCouponDiscountTarget && this.hasValidReferralCoupon()) {
      this.referralCouponDiscountTarget.innerHTML = `🎉 [${this.referralCouponValue.name}] 첫 결제 할인: ${this.referralCouponValue.display_discount} 할인 적용됩니다`;;
      this.referralCouponDiscountTarget.classList.remove("hidden");
    }
  }

  hideReferralCouponDiscount() {
    if (this.hasReferralCouponDiscountTarget) {
      this.referralCouponDiscountTarget.classList.add("hidden");
    }
  }

  connect() {
    this.afterPlanTypeSelected();
  }

  disconnect() {

  }
}
